import { useState } from 'react';

import { Upload, AlertTriangle, Calendar, CheckCircle, Loader2, ArrowLeft, ArrowRight } from 'lucide-react';

import { SettingsWrapper } from './SettingsWrapper';
import UploadFailuresTable from './UploadFailuresTable';
import formatDate from '../../../../../helpers/formatDate';
import { useGetUploadHistory } from '@/api/queries/useGetUploadHistory';
import { UploadMetadata } from '@/api/types/backend-dto-types';
import { Button } from '@/components/atoms/new-button';
import EmptyState from '@/components/molecules/EmptyState';

interface UploadsOverviewProps {
  caseId: string;
}

export const UploadsOverview = ({ caseId }: UploadsOverviewProps) => {
  const { data: uploads, isLoading } = useGetUploadHistory(caseId);
  const [showFailures, setShowFailures] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState<UploadMetadata | null>(null);

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  if (showFailures && selectedUpload) {
    return (
      <SettingsWrapper>
        <div className="flex flex-col">
          <div className="mb-4">
            <div className="mb-4 flex items-center justify-between gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowFailures(false);
                  setSelectedUpload(null);
                }}
                className="h-auto px-2 py-1 text-gray-600 hover:text-gray-800"
              >
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back to Upload History
              </Button>
              <span className="text-sm text-gray-500">Upload ID: {selectedUpload?.id}</span>
            </div>

            <h2 className="mb-2 text-base font-semibold">Upload Failures</h2>
            <p className="text-sm text-gray-500">
              Files that failed to upload on {formatDate(selectedUpload?.createdAt, true, true)}.
            </p>
          </div>

          <UploadFailuresTable upload={selectedUpload} />
        </div>
      </SettingsWrapper>
    );
  }

  return (
    <SettingsWrapper>
      <div className="mb-4">
        <div className="mb-2 flex items-center gap-2">
          <h2 className="text-base font-semibold">Upload History</h2>
        </div>
        <p className="text-sm text-gray-500">
          View all document uploads for this case, including any files that failed to upload.
        </p>
        <p className="mt-1 text-xs text-gray-500">
          An upload is when you attempt to upload files to Wexler, not when you submit them for processing, submitting
          files is a <b>Run</b>.
        </p>
      </div>

      <div className="h-full overflow-y-auto rounded-md border border-gray-200 bg-white">
        <div className="divide-y divide-gray-100">
          {isLoading ? (
            <div className="mt-14 flex h-full flex-col items-center justify-center gap-2">
              <Loader2 className="animate-spin text-gray-500" />
              <p className="text-sm text-gray-500">Loading upload history...</p>
            </div>
          ) : !uploads || uploads.length === 0 ? (
            <div className="px-4">
              <EmptyState
                title="No uploads found"
                subtitle="Upload history will appear here once you add documents to this case. Note: upload history is only available for uploads since 29th September 2025."
                icon={<Upload className="h-6 w-6 text-gray-800" />}
              />
            </div>
          ) : (
            <>
              {uploads.map((upload, index) => (
                <div key={upload.id} className="group flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="flex h-7 w-7 items-center justify-center rounded bg-gray-100 text-xs font-semibold text-gray-700 group-hover:bg-gray-200">
                      {index + 1}
                    </div>
                    <div className="flex flex-1 flex-col gap-1">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-800">
                          {formatNumber(upload.successfullyUploadedFileCount ?? 0)}{' '}
                          {upload.successfullyUploadedFileCount === 1 ? 'file' : 'files'} uploaded
                        </span>
                        <span className="rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-blue-200">
                          {upload.createdBy.email}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(upload.createdAt, true, true)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 flex items-center gap-4">
                    {upload.uploadFailures.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedUpload(upload);
                          setShowFailures(true);
                        }}
                        className="group h-auto border border-orange-200 px-2 py-1 text-xs text-orange-600 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-800"
                      >
                        <AlertTriangle className="mr-1 h-3 w-3" />
                        {formatNumber(upload.uploadFailures.length)} files failed
                        <ArrowRight className="ml-1 h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5" />
                      </Button>
                    )}
                    {upload.uploadFailures.length > 0 ? (
                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                    ) : (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </SettingsWrapper>
  );
};
